<script setup lang="ts">
import {onMounted} from "vue";
import type {Tag} from "@/Types";
import {AutoComplete, Button, Checkbox, Label, RadioButton, SelectButton, Slider, ToggleSwitch} from "@/Components/Library";
import DatePicker from "primevue/datepicker";
import MultiSelect from "primevue/multiselect";
import {QuestionTypes} from "@/Enums";
import {useTags} from "@/Composables/useTags";

const {groups, limits, totalActiveFilters} = defineProps<{
    groups: any;
    limits: {responses_count: number; groups_count: number};
    totalActiveFilters?: number;
}>();

const filters = defineModel<{
    ownership: "all" | "mine" | "others";
    group_id: string[];
    tags: Tag[];
    strict_tags: boolean;
    created_at: Date[] | null;
    publish_from: Date[] | null;
    publish_to: Date[] | null;
    responses_count: [number, number];
    groups_count: [number, number];
    type: Record<string, boolean>;
    state: "created" | "archived";
}>();
const emit = defineEmits(["apply", "clear"]);

const {tags, fetchTags, loading} = useTags();

onMounted(() => {
    void fetchTags();
});
</script>

<template>
    <div>
        <div class="mb-4">
            <h3 class="mb-1 text-sm font-semibold">Ownership</h3>
            <SelectButton
                v-model="filters.ownership"
                :options="[
                    {label: 'Mine', value: 'mine'},
                    {label: 'Others', value: 'others'},
                ]"
                option-label="label"
                option-value="value"
                class="w-full"
            />
        </div>
        <div class="mb-4">
            <h3 class="mb-1 text-sm font-semibold">Group(s)</h3>
            <MultiSelect
                v-model="filters.group_id"
                :options="groups"
                option-label="name"
                option-value="id"
                placeholder="Select groups"
                class="w-full"
                fluid
                display="chip"
            />
        </div>
        <div class="mb-4">
            <h3 class="mb-1 text-sm font-semibold">Tags</h3>
            <AutoComplete
                input-id="tags_selector"
                multiple
                :items="tags"
                v-model="filters.tags"
                label="Filter by tags"
                placeholder="Start typing to filter tags"
                dropdown
                fluid
            />
            <p v-if="loading" class="mt-1 text-xs text-stone-500">Loading tags...</p>
            <div class="mt-2 flex items-center gap-2">
                <ToggleSwitch input-id="strict_tags" v-model="filters.strict_tags" />
                <Label for="strict_tags">Strict match (must include all selected tags)</Label>
            </div>
        </div>
        <div class="mb-4">
            <h3 class="mb-1 text-sm font-semibold">Created (Range)</h3>
            <DatePicker
                placeholder="Select a date range"
                v-model="filters.created_at"
                show-time
                selectionMode="range"
                date-format="dd/mm/yy"
                hour-format="24"
                :hideOnDateTimeSelect="true"
                fluid
                showClear
            />
        </div>
        <div class="mb-4">
            <h3 class="mb-1 text-sm font-semibold">Published (Range)</h3>
            <DatePicker
                placeholder="Select a date range"
                v-model="filters.publish_from"
                show-time
                selectionMode="range"
                date-format="dd/mm/yy"
                hour-format="24"
                :hideOnDateTimeSelect="true"
                fluid
                showClear
            />
        </div>
        <div class="mb-4">
            <h3 class="mb-1 text-sm font-semibold">Deadline (Range)</h3>
            <DatePicker
                placeholder="Select a date range"
                v-model="filters.publish_to"
                show-time
                selectionMode="range"
                date-format="dd/mm/yy"
                hour-format="24"
                :hideOnDateTimeSelect="true"
                fluid
                showClear
            />
        </div>
        <div class="mb-4">
            <h3 class="mb-1 text-sm font-semibold">Number of responses</h3>
            <div class="flex items-center gap-4">
                <span>{{ filters.responses_count[0] }}</span>
                <Slider v-model="filters.responses_count" :min="0" :max="limits.responses_count" range />
                <span>{{ filters.responses_count[1] }}</span>
            </div>
        </div>
        <div class="mb-4">
            <h3 class="mb-1 text-sm font-semibold">Number of groups</h3>
            <div class="flex items-center gap-4">
                <span>{{ filters.groups_count[0] }}</span>
                <Slider v-model="filters.groups_count" :min="0" :max="limits.groups_count" range />
                <span>{{ filters.groups_count[1] }}</span>
            </div>
        </div>
        <div class="mb-4">
            <h3 class="mb-2 font-semibold">Post type</h3>
            <div class="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <template v-for="type in QuestionTypes" :key="type.slug">
                    <div class="flex items-center gap-2">
                        <Checkbox
                            :input-id="`type_${type.slug}`"
                            :name="type.slug"
                            binary
                            v-model="filters.type[type.slug]"
                        />
                        <Label :for="`type_${type.slug}`">{{ type.name }}</Label>
                    </div>
                </template>
            </div>
        </div>
        <div class="mb-4">
            <h3 class="mb-1 text-sm font-semibold">Post status</h3>
            <div class="flex flex-col gap-3 md:flex-row">
                <div class="flex items-center gap-2">
                    <RadioButton
                        input-id="created_questions"
                        name="created_questions"
                        v-model="filters.state"
                        value="created"
                    />
                    <Label for="created_questions">Created</Label>
                </div>
                <div class="flex items-center gap-2">
                    <RadioButton
                        input-id="archived_questions"
                        name="archived_questions"
                        v-model="filters.state"
                        value="archived"
                    />
                    <Label for="archived_questions">Archived</Label>
                </div>
            </div>
        </div>
        <div class="flex gap-2" v-auto-animate>
            <Button class="grow bg-askr-green text-white hover:brightness-110" @click="emit('apply')">Apply</Button>
            <Button
                v-if="totalActiveFilters"
                class="w-10 shrink-0 bg-red-600 text-white hover:brightness-110"
                @click="emit('clear')"
                :icon="{icon: 'mdi:filter-off-outline', width: '24'}"
            />
        </div>
    </div>
</template>

<style scoped></style>
